import React, { useEffect, useRef } from 'react';
import { Col, message, Modal, Row, Divider, Input, Select } from 'antd';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import FormField from '../FormField/FormField';
import { z } from 'zod';
import {
  useEditBusinessInfoMutation,
  useGetSingleRequestQuery,
} from '../../store/services/business-requests';
import {
  useGetCountriesQuery,
  useGetCountryStatesQuery,
  useGetCompanyTypesQuery,
  useGetCountriesDetailsQuery,
} from '../../store/services/lookups';
import Button from '../atoms/Button';
import { useSharedValidations } from '../../hooks/useSharedValidations';
import { getFirstValidationError } from '../../lib/apiErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import YadLogo from '../../assets/icons/YadLogo';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';
import { Edit2 } from 'iconsax-react';
import FlagComponent from 'react-world-flags';

const Flag = FlagComponent as React.ComponentType<{
  code: string;
  width?: string;
  height?: string;
}>;

interface EditModalProps {
  isEditOpen: boolean;
  handleModalClose: (closedAfterSuccess?: boolean) => void;
  requestId: string;
}

const EditModal = ({
  isEditOpen,
  handleModalClose,
  requestId,
}: EditModalProps) => {
  const { t } = useTranslation();

  const { required_error } = useSharedValidations();

  const { data: countries = [] } = useGetCountriesQuery();
  const { data: countriesDetails = [] } = useGetCountriesDetailsQuery();
  const { data: companyTypes = [] } = useGetCompanyTypesQuery();
  const { data: requestDetails } = useGetSingleRequestQuery(
    { requestId },
    { skip: !requestId }
  );
  const [editBusinessInfo] = useEditBusinessInfoMutation();

  console.log('countriesDetails', countriesDetails);

  const hasArabicScript = (s: string | null | undefined) =>
    !!s?.trim() &&
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(
      s
    );
  const hasLatinScript = (s: string | null | undefined) =>
    !!s?.trim() && /[a-zA-Z]/.test(s);

  const validateSchema = z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
      countryId: z.string({ required_error }),
      state: z.string(),
      stateId: z.string(),
      street: z.string().optional().nullable(),
      phoneNumber: z.object({
        code: z.string(),
        number: z.string(),
      }),
      dateOfBirth: z
        .union([z.string(), z.custom<dayjs.Dayjs>((val) => dayjs.isDayjs(val))])
        .transform((val) =>
          val == null
            ? undefined
            : typeof val === 'string'
            ? val
            : (val as dayjs.Dayjs).format('YYYY-MM-DD')
        )
        .optional()
        .nullable(),
      companyName: z.string(),
      companyType: z.string(),
      taxNumber: z.string(),
      commercialRegister: z
        .string({ required_error })
        .min(1, { message: t('components.validations.requiredField') })
        .regex(/^[0-9]+$/, {
          message: t('components.validations.acceptNumbersOnly'),
        })
        .regex(/^\d{4,}$/, {
          message: t('components.validations.commercialRegisterLength'),
        })
        .max(15, {
          message: t('components.validations.commercialRegisterLength'),
        }),
    })
    .refine(
      (data) => {
        const first = data.firstName;
        const last = data.lastName;
        if (hasArabicScript(first) && hasLatinScript(last)) return false;
        if (hasLatinScript(first) && hasArabicScript(last)) return false;
        return true;
      },
      {
        message: t('components.validations.namesMustBeSame'),
        path: ['lastName'],
      }
    );

  type formFieldType = z.infer<typeof validateSchema>;

  const methods = useForm<formFieldType>({
    resolver: zodResolver(validateSchema),
  });

  const countryId = methods.watch('countryId');
  const { data: states = [] } = useGetCountryStatesQuery(
    { countryId: countryId ?? '' },
    { skip: !countryId }
  );

  const prevCountryIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      prevCountryIdRef.current !== undefined &&
      prevCountryIdRef.current !== countryId
    ) {
      methods.setValue('stateId', '');
    }
    prevCountryIdRef.current = countryId;
  }, [countryId, methods]);

  const onSubmit = (data: formFieldType) => {
    const id = requestId || '0';
    editBusinessInfo({
      id,
      firstName: data.firstName,
      lastName: data.lastName,
      dob: data.dateOfBirth,
      phoneCode: data.phoneNumber?.code,
      phoneNumber: data.phoneNumber?.number,
      companyName: data.companyName,
      commercialRegister: data.commercialRegister,
      companyType: data.companyType,
      taxNumber: data.taxNumber,
      address: {
        ...(data?.street ? { street: data?.street } : {}),
        stateId: data?.stateId,
        countryId: data.countryId,
      },
    })
      .unwrap()
      .then(() => {
        methods.reset();
        handleModalClose(true);
        message.success(t('components.editsSentToApplicant'));
      })
      .catch((error) => {
        const firstError = getFirstValidationError(error);
        message.error(firstError ?? t('home.somethingWentWrong'));
      });
  };

  useEffect(() => {
    const payload = requestDetails?.payload;
    if (!payload) return;
    const { applicant, company, address } = payload;
    methods.setValue('companyName', company?.name);
    methods.setValue('email', applicant?.email);
    methods.setValue('phoneNumber.code', applicant?.phoneCode ?? '');
    methods.setValue('phoneNumber.number', applicant?.phoneNumber ?? '');
    methods.setValue('firstName', applicant?.firstName);
    methods.setValue('lastName', applicant?.lastName);
    methods.setValue('commercialRegister', company?.commercialRegister);
    methods.setValue('companyType', company?.companyTypeId ?? '');
    const countryIdVal = address?.countryId;
    if (countryIdVal != null) {
      methods.setValue('countryId', String(countryIdVal));
    }
    methods.setValue('state', address?.stateName ?? '');
    methods.setValue('stateId', address?.stateId ?? '');
    methods.setValue('street', address?.street ?? '');
    const dobStr = applicant?.dob;
    methods.setValue(
      'dateOfBirth',
      (dobStr && dayjs(dobStr).isValid()
        ? dayjs(dobStr)
        : undefined) as formFieldType['dateOfBirth']
    );
    methods.setValue('taxNumber', company?.taxNumber);
  }, [requestDetails]);

  return (
    <Modal
      footer={null}
      closable={true}
      destroyOnHidden
      open={isEditOpen}
      centered
      onCancel={() => handleModalClose()}
      width={600}
      data-test="edit-modal"
    >
      {/* Header */}
      <div
        className="flex items-start justify-between mb-2"
        data-test="edit-modal-header"
      >
        <div>
          <TitleText text={t('home.editApplicantRequest')} size="xl" />
          <InfoText
            text={t('home.editApplicantRequestDescription')}
            size="xs"
            maxLength="FULL"
            className="mt-1"
          />
        </div>
        <YadLogo width={40} height={40} />
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          data-test="edit-modal-form"
        >
          {/* Company Information */}
          <TitleText
            text={t('home.companyInformation')}
            size="base"
            className="!font-semibold mt-4 mb-3"
          />

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                placeholder={t('home.firstName').toLowerCase()}
                label={t('home.firstName')}
                type="text"
                name="firstName"
                id="firstName"
                labelClassName="text-sm"
              />
            </Col>
            <Col span={12}>
              <FormField
                placeholder={t('home.lastName').toLowerCase()}
                label={t('home.lastName')}
                type="text"
                name="lastName"
                id="lastName"
                labelClassName="text-sm"
              />
            </Col>
          </Row>

          <div className="mt-3">
            <FormField
              placeholder="info@mail.com"
              label={t('home.email')}
              type="text"
              name="email"
              id="email"
              labelClassName="text-sm"
              disabled
            />
          </div>

          <Row gutter={16} className="mt-3">
            <Col span={12}>
              <label className="block text-sm font-medium text-light-text-primary mb-1.5">
                {t('home.phoneNumber')}
              </label>
              <div
                className="flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden h-10"
                data-test="edit-modal-phone-input"
              >
                <Controller
                  name="phoneNumber.code"
                  control={methods.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={
                        field.value != null
                          ? field.value.replace(/^\+/, '')
                          : undefined
                      }
                      onSelect={(v) => field.onChange(v ? `+${v}` : v)}
                      options={(countriesDetails ?? []).map((c) => ({
                        value: c.phoneCode,
                        label: (
                          <span className="flex items-center gap-2">
                            <Flag code={c.iso2} width="20" height="14" />
                            <span>+{c.phoneCode}</span>
                          </span>
                        ),
                      }))}
                      disabled
                      className="!min-w-[110px] [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!shadow-none [&_.ant-select-selection-item]:flex [&_.ant-select-selection-item]:items-center [&_.ant-select-selection-item]:gap-2"
                    />
                  )}
                />
                <Controller
                  name="phoneNumber.number"
                  control={methods.control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="XXXX - XXX - XXXX"
                      variant="borderless"
                      className="!flex-1 !h-9 !py-0 !rounded-none"
                      disabled
                    />
                  )}
                />
              </div>
            </Col>
            <Col span={12}>
              <FormField
                placeholder="dd/mm/yyyy"
                label={t('home.dateOfBirth')}
                type="date"
                name="dateOfBirth"
                id="dateOfBirth"
                labelClassName="text-sm"
              />
            </Col>
          </Row>

          <Row gutter={16} className="mt-3">
            <Col span={12}>
              <FormField
                placeholder={t('home.companyName').toLowerCase()}
                label={t('home.companyName')}
                type="text"
                name="companyName"
                id="companyName"
                labelClassName="text-sm"
              />
            </Col>
            <Col span={12}>
              <FormField
                type="select"
                options={companyTypes.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id="companyType"
                name="companyType"
                label={t('home.companyType')}
                placeholder={t('home.chooseYourType')}
                labelClassName="text-sm"
              />
            </Col>
          </Row>

          <Divider className="my-4" />

          {/* Address Information */}
          <TitleText
            text={t('home.addressInformation')}
            size="base"
            className="!font-semibold mb-3"
          />

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                showSearch
                type="select"
                options={(countries ?? []).map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id="countryId"
                name="countryId"
                label={t('home.country')}
                placeholder={t('home.searchYourCountry')}
                labelClassName="text-sm"
              />
            </Col>
            <Col span={12}>
              <FormField
                showSearch
                type="select"
                options={states.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id="stateId"
                name="stateId"
                label={t('home.state')}
                placeholder={t('home.yourState')}
                labelClassName="text-sm"
                disabled={!countryId}
              />
            </Col>
          </Row>

          <div className="mt-3">
            <FormField
              id="street"
              name="street"
              type="text"
              label={
                <span className="text-sm">
                  {t('home.streetAddress')}{' '}
                  <span className="text-gray-400 text-xs">
                    ({t('home.optional')})
                  </span>
                </span>
              }
              placeholder={t('home.yourStreetInformation')}
              labelClassName="text-sm"
            />
          </div>

          <Divider className="my-4" />

          {/* Company Papers */}
          <TitleText
            text={t('home.companyPapers')}
            size="base"
            className="!font-semibold mb-3"
          />

          <Row gutter={16}>
            <Col span={12}>
              <FormField
                placeholder={t('home.taxNumber').toLowerCase()}
                label={t('home.taxNumber')}
                type="text"
                name="taxNumber"
                id="taxNumber"
                labelClassName="text-sm"
              />
            </Col>
            <Col span={12}>
              <FormField
                placeholder={t('home.commercialRegisterNumber')}
                label={t('home.commercialRegister')}
                type="text"
                name="commercialRegister"
                id="commercialRegister"
                labelClassName="text-sm"
              />
            </Col>
          </Row>

          <div
            className="flex justify-center gap-3 mt-6"
            data-test="edit-modal-actions"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => handleModalClose()}
              data-test="edit-modal-cancel-btn"
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="submit"
              variant="info"
              icon={<Edit2 size={20} color="#ffffff" variant="Bold" />}
              className="w-40"
              data-test="edit-modal-submit-btn"
            >
              {t('home.edit')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default EditModal;
